import {useState, useEffect, useRef} from 'react'
import type {Participant, LocalParticipant, RemoteParticipant} from 'livekit-client'
import {Track} from 'livekit-client'

// RMS threshold — must be above Opus codec noise floor + WebRTC comfort noise (~0.001–0.005)
const AUDIO_THRESHOLD = 0.0001
// Silence delay to avoid flickering (ms)
const SILENCE_DELAY_MS = 100

interface ParticipantAudioNode {
  analyser: AnalyserNode
  source: MediaStreamAudioSourceNode
  trackId: string
}

/**
 * Extract the microphone MediaStreamTrack from a participant
 */
function getMicTrack(participant: Participant): MediaStreamTrack | null {
  const pub = participant.isLocal
    ? (participant as LocalParticipant).getTrackPublication(Track.Source.Microphone)
    : (participant as RemoteParticipant).getTrackPublication(Track.Source.Microphone)
  return pub?.audioTrack?.mediaStreamTrack ?? null
}

/**
 * Hook to monitor speaking state of multiple participants using a single
 * shared AudioContext and a single requestAnimationFrame loop.
 *
 * Much more efficient than per-participant hooks — avoids creating N AudioContexts
 * (browsers limit to ~6) and N rAF loops.
 *
 * @returns Map where key is participant identity, value is speaking state.
 *          Only entries for participants whose state has been analyzed are present.
 */
export function useMultiParticipantSpeaking(participants: Participant[]): Map<string, boolean> {
  const [speakingMap, setSpeakingMap] = useState<Map<string, boolean>>(() => new Map())

  const audioCtxRef = useRef<AudioContext | null>(null)
  const nodesRef = useRef(new Map<string, ParticipantAudioNode>())
  const speakingStateRef = useRef(new Map<string, boolean>())
  const silenceTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const rafRef = useRef<number | null>(null)
  const participantsRef = useRef(participants)
  participantsRef.current = participants

  // Shared Float32Array buffer — avoids allocation per frame per participant
  const dataBufferRef = useRef<Float32Array | null>(null)

  useEffect(() => {
    // Create shared AudioContext (one for ALL participants)
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext()
    }
    const audioCtx = audioCtxRef.current
    if (audioCtx.state === 'suspended') audioCtx.resume()

    /** Cleanup audio nodes for a single participant */
    const cleanupNodes = (identity: string) => {
      const node = nodesRef.current.get(identity)
      if (node) {
        node.source.disconnect()
        node.analyser.disconnect()
        nodesRef.current.delete(identity)
      }
      const timer = silenceTimersRef.current.get(identity)
      if (timer) {
        clearTimeout(timer)
        silenceTimersRef.current.delete(identity)
      }
    }

    /** Setup AnalyserNode + MediaStreamSource for a participant's mic track */
    const setupNodes = (identity: string, track: MediaStreamTrack) => {
      const existing = nodesRef.current.get(identity)
      if (existing?.trackId === track.id) return // Already set up for this track

      cleanupNodes(identity)

      const analyser = audioCtx.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.2 // Lower = faster response

      const source = audioCtx.createMediaStreamSource(new MediaStream([track]))
      source.connect(analyser)

      nodesRef.current.set(identity, {analyser, source, trackId: track.id})
    }

    /** Batch-update React state — only creates new Map when there are actual changes */
    const flushUpdates = (updates: Map<string, boolean>) => {
      if (updates.size === 0) return
      setSpeakingMap(prev => {
        let changed = false
        for (const [id, speaking] of updates) {
          if (prev.get(id) !== speaking) {
            changed = true
            break
          }
        }
        if (!changed) return prev
        const next = new Map(prev)
        for (const [id, speaking] of updates) {
          next.set(id, speaking)
        }
        return next
      })
    }

    /** Single rAF loop — processes ALL participants in one pass */
    const tick = () => {
      const currentParticipants = participantsRef.current
      const currentIds = new Set(currentParticipants.map(p => p.identity))

      // Remove departed participants' audio nodes
      for (const identity of nodesRef.current.keys()) {
        if (!currentIds.has(identity)) {
          cleanupNodes(identity)
          speakingStateRef.current.delete(identity)
        }
      }

      // Ensure shared data buffer exists
      if (!dataBufferRef.current) {
        dataBufferRef.current = new Float32Array(256)
      }
      const dataArray = dataBufferRef.current

      const batchUpdates = new Map<string, boolean>()

      for (const p of currentParticipants) {
        const id = p.identity
        const track = getMicTrack(p)

        if (!track) {
          // No audio track — cleanup nodes and mark not speaking if needed
          if (nodesRef.current.has(id)) {
            cleanupNodes(id)
            if (speakingStateRef.current.get(id)) {
              speakingStateRef.current.set(id, false)
              batchUpdates.set(id, false)
            }
          }
          continue
        }

        // Setup analyser if needed (handles track changes automatically)
        setupNodes(id, track)

        const node = nodesRef.current.get(id)
        if (!node) continue

        // Analyze audio level via RMS
        node.analyser.getFloatTimeDomainData(dataArray)
        let sum = 0
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i] * dataArray[i]
        }
        const rms = Math.sqrt(sum / dataArray.length)
        const speaking = rms > AUDIO_THRESHOLD

        if (speaking) {
          // Clear silence timer if any
          const timer = silenceTimersRef.current.get(id)
          if (timer) {
            clearTimeout(timer)
            silenceTimersRef.current.delete(id)
          }
          // Mark as speaking if not already
          if (!speakingStateRef.current.get(id)) {
            speakingStateRef.current.set(id, true)
            batchUpdates.set(id, true)
          }
        } else if (speakingStateRef.current.get(id) && !silenceTimersRef.current.has(id)) {
          // Silence detected — start countdown before marking as not speaking
          silenceTimersRef.current.set(
            id,
            setTimeout(() => {
              silenceTimersRef.current.delete(id)
              speakingStateRef.current.set(id, false)
              setSpeakingMap(prev => {
                if (prev.get(id) === false) return prev
                const next = new Map(prev)
                next.set(id, false)
                return next
              })
            }, SILENCE_DELAY_MS),
          )
        }
      }

      flushUpdates(batchUpdates)
      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)

    const nodes = nodesRef.current
    const speakingState = speakingStateRef.current
    const silenceTimers = silenceTimersRef.current

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      for (const identity of [...nodes.keys()]) {
        cleanupNodes(identity)
      }
      nodes.clear()
      speakingState.clear()
      for (const timer of silenceTimers.values()) {
        clearTimeout(timer)
      }
      silenceTimers.clear()
    }
  }, []) // Empty deps — participants tracked via ref

  // Clean up state entries for departed participants
  useEffect(() => {
    const currentIds = new Set(participants.map(p => p.identity))
    setSpeakingMap(prev => {
      let changed = false
      const next = new Map(prev)
      for (const id of prev.keys()) {
        if (!currentIds.has(id)) {
          next.delete(id)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [participants])

  // Close AudioContext on unmount
  useEffect(() => {
    return () => {
      if (audioCtxRef.current) {
        audioCtxRef.current.close()
        audioCtxRef.current = null
      }
    }
  }, [])

  return speakingMap
}
